import { useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  useAdoptIdea,
  useCommunityIdeas,
  useCommunityPulse,
  useCreateIdea,
  useSession,
  useToggleIdeaLike,
} from "@/api/queries";
import type { CommunityIdea } from "@/api/types";
import { EmptyState } from "@/components/EmptyState";
import { ErrorState } from "@/components/ErrorState";
import { Panel } from "@/components/Panel";
import { RemoteState } from "@/components/RemoteState";
import { SegmentControl } from "@/components/SegmentControl";
import { Tag } from "@/components/Tag";
import { formatNumber } from "@/lib/cn";
import { useUiStore } from "@/store/ui";

type SortMode = "recommend" | "latest";

/**
 * 共创社区（原型 `renderCommunity`）。三层权限漏斗保证创作质量。
 *
 * **`mixed`：骨架静态 + 脑洞真实**（RULES §11.3 · `specs/15` §11）——
 *
 * - **静态**：三层漏斗**说明文案**（产品叙事，`specs/15` §10 第 8 条）。
 * - **远端**：脑洞**列表 / 发布 / 点赞 / 采纳**接 `/community/*`：
 *   发布真实落库（刷新不丢）、点赞是 toggle、赞同数由后端现算。
 *   「本周共创数据」脉冲只显示**两个有来源的数字**（本周去重观众 / 进入候选池，
 *   `GET /community/pulse`）；原型的另两个（"半专业整理" / "被项目采用"）**没有来源**，
 *   按 CONSTITUTION D2 **不显示**——不是把它们写小，而是不出现（§10 第 8 条）。
 *
 * **权限由服务端决定**（前端不是权限边界）：
 * - 读公开；发布与点赞需登录（未登录时前端引导去 `/login`）；
 * - **采纳为候选＝创作身份专属**——player / 游客不显示该按钮，后端仍会 `403`（PR-1514）。
 *
 * @dataSource mixed
 */
export function CommunityView() {
  const navigate = useNavigate();
  const pushToast = useUiStore((state) => state.pushToast);
  const [sort, setSort] = useState<SortMode>("recommend");
  const [draft, setDraft] = useState("");

  const session = useSession();
  const ideasQuery = useCommunityIdeas(sort);
  const pulseQuery = useCommunityPulse();
  const createIdea = useCreateIdea();
  const toggleLike = useToggleIdeaLike();
  const adoptIdea = useAdoptIdea();

  const user = session.data ?? null;
  const isLoggedIn = Boolean(user);
  const isEditor = user?.platform_role === "editor";
  const ideas = ideasQuery.data?.items ?? [];
  const pulse = pulseQuery.data;

  /** 写操作需登录：未登录就地引导去登录页（不静默失败）。 */
  function requireLogin(action: string): boolean {
    if (isLoggedIn) return true;
    pushToast({ title: "请先登录", detail: `登录后才能${action}`, mark: "!" });
    navigate("/login");
    return false;
  }

  function publish() {
    const text = draft.trim();
    if (!text || !requireLogin("发布脑洞")) return;
    createIdea.mutate(
      { text },
      {
        onSuccess: () => {
          setDraft("");
          pushToast({
            title: "脑洞已发布",
            detail: "它已真实落库，等待创作者整理与专业编剧审核",
            mark: "✓",
          });
        },
      },
    );
  }

  function like(idea: CommunityIdea) {
    if (!requireLogin("点赞")) return;
    toggleLike.mutate(idea.id, {
      onSuccess: (result) =>
        pushToast({
          title: result.liked ? "已赞同" : "已取消赞同",
          detail: `${idea.title} · 当前 ${result.likes} 人赞同`,
          mark: result.liked ? "✓" : "·",
        }),
    });
  }

  function adopt(idea: CommunityIdea) {
    adoptIdea.mutate(idea.id, {
      onSuccess: () =>
        pushToast({
          title: "已标记为候选",
          detail: "进入候选池不等于进入正式版本，仍需专业编剧审核与结构检查",
          mark: "✓",
        }),
    });
  }

  return (
    <div className="platform-scroll community-view">
      <div className="community-width">
        <header className="page-heading">
          <div>
            <span className="eyebrow">共创社区 · 观众与创作者共享</span>
            <h2>你不必会写完整分支，也能让创作者听见故事的另一种可能</h2>
            <p>
              游玩选择、讨论与一句话脑洞形成需求信号；半专业创作者整理为候选；专业编剧审核后，才可能进入正式作品。
            </p>
          </div>
          <button
            className="button button-primary"
            type="button"
            onClick={() => navigate("/discover")}
          >
            先去玩一个故事
          </button>
        </header>

        <section className="community-pulse" aria-label="本周共创数据">
          {pulseQuery.isError ? (
            <ErrorState title="本周共创数据加载失败" error={pulseQuery.error} />
          ) : (
            <>
              <div>
                <span>本周参与观众</span>
                <strong>{pulse ? formatNumber(pulse.weekly_audience) : "—"}</strong>
                <small>本周内产生过互动的去重观众</small>
              </div>
              <div>
                <span>进入候选池</span>
                <strong>{pulse ? formatNumber(pulse.candidate_pool) : "—"}</strong>
                <small>已被创作者采纳为候选的脑洞数</small>
              </div>
            </>
          )}
        </section>

        <div className="community-layout">
          <Panel
            title="正在被认真讨论的脑洞"
            description="跨项目推荐 · 按讨论质量和相关度排序"
            actions={
              <SegmentControl<SortMode>
                label="脑洞排序"
                value={sort}
                onChange={setSort}
                options={[
                  { value: "recommend", label: "推荐" },
                  { value: "latest", label: "最新" },
                ]}
              />
            }
            bodyClassName="idea-list"
          >
            <RemoteState queries={[ideasQuery]} title="脑洞加载失败" loadingText="正在加载脑洞…">
              {ideas.length === 0 ? (
                <EmptyState
                  title="还没有脑洞"
                  description="成为第一个留下想法的人——发布后会真实保存，刷新也不会丢。"
                />
              ) : (
                <>
                  {ideas.map((idea) => (
                    <IdeaCard
                      key={idea.id}
                      idea={idea}
                      canAdopt={isEditor}
                      busy={toggleLike.isPending || adoptIdea.isPending}
                      onLike={() => like(idea)}
                      onAdopt={() => adopt(idea)}
                    />
                  ))}
                </>
              )}
            </RemoteState>
          </Panel>

          <aside>
            <section className="idea-composer">
              <span className="eyebrow">普通观众也能参与</span>
              <h3>我也有一个想法</h3>
              <textarea
                value={draft}
                placeholder="写下一两句话：这个角色还可能怎么做？"
                aria-label="写下一两句话脑洞"
                onChange={(event) => setDraft(event.target.value)}
              />
              <footer>
                <span style={{ color: "var(--ink-faint)", fontSize: "9px" }}>
                  发布后进入候选池，不改变正式版本
                </span>
                <button
                  className="button button-primary"
                  type="button"
                  disabled={draft.trim().length === 0 || createIdea.isPending}
                  onClick={publish}
                >
                  {createIdea.isPending ? "发布中…" : "发布脑洞"}
                </button>
              </footer>
            </section>

            <Panel
              title="脑洞如何变成内容"
              description="三层权限保障创作质量"
              bodyClassName="funnel"
              className="community-funnel"
            >
              <div className="funnel-step">
                <strong>普通观众</strong>
                <span>选择、评论与一句话脑洞形成需求信号</span>
              </div>
              <div className="funnel-step">
                <strong>半专业创作者</strong>
                <span>整理成候选分支、人物设定或Storylet</span>
              </div>
              <div className="funnel-step">
                <strong>专业编剧 / IP方</strong>
                <span>审核、修改并决定正式版本</span>
              </div>
            </Panel>
          </aside>
        </div>
      </div>
    </div>
  );
}

function IdeaCard({
  idea,
  canAdopt,
  busy,
  onLike,
  onAdopt,
}: {
  idea: CommunityIdea;
  /** 是否显示「采用为候选」——仅创作身份（服务端权限的唯一呈现面，非权限边界）。 */
  canAdopt: boolean;
  busy: boolean;
  onLike: () => void;
  onAdopt: () => void;
}) {
  return (
    <article className="idea-card">
      <div className="meta-row">
        <Tag>{idea.ip ?? "共创"}</Tag>
        <Tag tone={idea.adopted ? "green" : "default"}>
          {idea.adopted ? "已进入候选" : idea.source}
        </Tag>
      </div>
      <h3>{idea.title}</h3>
      <p>{idea.text}</p>
      <div className="idea-meta">
        <span>
          {idea.heat.toLocaleString("zh-CN")} 人赞同 · {idea.source}
        </span>
        <span style={{ display: "flex", gap: "8px" }}>
          <button
            className={`button button-small ${idea.liked_by_me ? "button-primary" : "button-quiet"}`}
            type="button"
            disabled={busy}
            onClick={onLike}
          >
            {idea.liked_by_me ? "已赞同" : "赞同"}
          </button>
          {canAdopt ? (
            <button
              className={`button button-small ${idea.adopted ? "button-quiet" : "button-primary"}`}
              type="button"
              disabled={idea.adopted || busy}
              onClick={onAdopt}
            >
              {idea.adopted ? "已采用" : "采用为候选"}
            </button>
          ) : null}
        </span>
      </div>
    </article>
  );
}
